import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import EmbedMatchCard from "./EmbedMatchCard";
import EmbedHeaderWidget from "./EmbedHeaderWidget";
import type { EmbedNav } from "./info";
import { asLayout, pickLayout, useViewportWidth } from "./layout";
import type { MatchVm } from "../tournamentPage/matchVm";
import { useEmbedMatch, useEmbedTournament } from "./useEmbedMatch";

/** The widget sits on someone else's page: no app chrome, transparent background. */
const useEmbedBody = () =>
  useEffect(() => {
    document.documentElement.classList.add("em-body-root");
    document.body.classList.add("em-body-root");
    return () => {
      document.documentElement.classList.remove("em-body-root");
      document.body.classList.remove("em-body-root");
    };
  }, []);

/** Picks the layout from the iframe's own width (the big card, or one of the small header widgets); ?layout=card|bug|compact|mini forces one. */
const EmbedView = ({ vm, now, polling, nav }: { vm: MatchVm; now: number; polling?: boolean; nav?: EmbedNav }) => {
  const [search] = useSearchParams();
  const width = useViewportWidth();
  const layout = asLayout(search.get("layout")) ?? pickLayout(width);
  return layout === "card" ? <EmbedMatchCard vm={vm} now={now} polling={polling} nav={nav} /> : <EmbedHeaderWidget layout={layout} vm={vm} now={now} nav={nav} />;
};

const Msg = ({ children }: { children: string }) => <div className="em-msg">{children}</div>;

/** /embed/match/:matchId : one match, no arrows. */
export const EmbedMatch = () => {
  useEmbedBody();
  const { matchId } = useParams();
  const { vm, now, polling, isLoading, isError } = useEmbedMatch(matchId);
  if (isLoading) return <Msg>Loading match…</Msg>;
  if (isError || !vm) return <Msg>This match could not be loaded.</Msg>;
  return <EmbedView vm={vm} now={now} polling={polling} />;
};

/** /embed/tournament/:tournamentId[?matchId=] : every match of the tournament, switched with the arrows. */
export const EmbedTournament = () => {
  useEmbedBody();
  const { tournamentId } = useParams();
  const [search] = useSearchParams();
  const { vms, initialId, loading, error } = useEmbedTournament(tournamentId, search.get("matchId"));
  const [pickedId, setPickedId] = useState<string | null>(null);

  const index = vms.findIndex((m) => m.id === (pickedId ?? initialId));
  const current = vms[index];
  // Poll only the match on screen; the list entry is the seed until its own fetch lands.
  const { vm, now, polling } = useEmbedMatch(current?.id, current?.raw);

  if (loading) return <Msg>Loading matches…</Msg>;
  if (error) return <Msg>Matches could not be loaded.</Msg>;
  if (!current || !vm) return <Msg>No matches in this tournament yet.</Msg>;

  const go = (to: number) => setPickedId(vms[to]?.id ?? null);
  const shown = { ...vm, no: current.no };
  return <EmbedView vm={shown} now={now} polling={polling} nav={{ index, total: vms.length, onPrev: () => go(index - 1), onNext: () => go(index + 1), onCycle: () => go((index + 1) % vms.length) }} />;
};
